/**
 * PURE Image Blocks Slider Functionality
 * Handles autoplay, navigation, and dot indicators for image block sliders
 */

class PureImageBlocksSlider {
  constructor(slider) {
    this.slider = slider;
    this.sliderList = slider.querySelector('.slider');
    this.dotLinks = slider.querySelectorAll('.slider-counter__link--dots');
    this.slides = slider.querySelectorAll('.pure-image-blocks__slide');
    this.prevButton = slider.querySelector('button[name="previous"]');
    this.nextButton = slider.querySelector('button[name="next"]');
    
    if (!this.sliderList || !this.dotLinks.length || !this.slides.length) return;
    
    // Autoplay functionality
    this.autoplayEnabled = slider.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(slider.dataset.speed) || 5) * 1000;
    this.autoplayInterval = null;
    this.isPaused = false;
    this.isAutoplayScrolling = false;
    this.manualNavigationTimeout = null;
    this.lastScrollLeft = this.sliderList.scrollLeft;
    
    this.init();
  }
  
  init() {
    // Handle next/previous button clicks
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.resetAutoplay();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.resetAutoplay();
      });
    }
    
    // Pause autoplay on hover/focus
    this.slider.addEventListener('mouseenter', () => {
      this.isPaused = true;
      this.stopAutoplay();
    });
    
    this.slider.addEventListener('mouseleave', () => {
      this.isPaused = false;
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });
    
    this.slider.addEventListener('focusin', () => {
      this.isPaused = true;
      this.stopAutoplay();
    });
    
    this.slider.addEventListener('focusout', () => {
      this.isPaused = false;
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });
    
    // Handle dot clicks
    this.dotLinks.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetAutoplay();
        const targetSlide = this.slides[index];
        if (targetSlide) {
          const scrollPosition = targetSlide.offsetLeft - this.sliderList.offsetLeft;
          this.sliderList.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      });
    });
    
    // Track manual scroll (not from autoplay)
    this.sliderList.addEventListener('scroll', () => {
      this.updateActiveDot();
      
      // Detect if this is a manual scroll (not from autoplay)
      if (!this.isAutoplayScrolling && Math.abs(this.sliderList.scrollLeft - this.lastScrollLeft) > 10) {
        // Significant scroll change that wasn't from autoplay = manual navigation
        this.resetAutoplay();
      }
      this.lastScrollLeft = this.sliderList.scrollLeft;
    });
    
    // Initial update
    this.updateActiveDot();
    
    // Start autoplay if enabled
    if (this.autoplayEnabled) {
      // Check for reduced motion preference
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!reducedMotion.matches) {
        this.startAutoplay();
      }
    }
  }
  
  startAutoplay() {
    if (!this.autoplayEnabled || this.isPaused) return;
    
    // Clear any existing interval
    this.stopAutoplay();
    
    this.autoplayInterval = setInterval(() => {
      if (this.isPaused) return;
      
      const scrollLeft = this.sliderList.scrollLeft;
      const slideWidth = this.slides[0].offsetWidth;
      const currentIndex = Math.round(scrollLeft / slideWidth);
      const nextIndex = (currentIndex + 1) % this.slides.length;
      
      const targetSlide = this.slides[nextIndex];
      if (targetSlide) {
        this.isAutoplayScrolling = true; // Mark that this scroll is from autoplay
        const scrollPosition = targetSlide.offsetLeft - this.sliderList.offsetLeft;
        this.sliderList.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
        // Reset flag after scroll animation completes
        setTimeout(() => {
          this.isAutoplayScrolling = false;
        }, 500);
      }
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoplay() {
    // Stop current autoplay
    this.stopAutoplay();
    // Clear any pending restart
    if (this.manualNavigationTimeout) {
      clearTimeout(this.manualNavigationTimeout);
      this.manualNavigationTimeout = null;
    }
    // Restart autoplay after delay if not paused
    if (this.autoplayEnabled && !this.isPaused) {
      this.manualNavigationTimeout = setTimeout(() => {
        this.startAutoplay();
      }, this.autoplaySpeed);
    }
  }
  
  updateActiveDot() {
    const scrollLeft = this.sliderList.scrollLeft;
    const containerWidth = this.sliderList.clientWidth;
    const centerPoint = scrollLeft + containerWidth / 2;
    
    // Find which slide is closest to the center
    let activeIndex = 0;
    let minDistance = Infinity;
    
    this.slides.forEach((slide, index) => {
      const slideLeft = slide.offsetLeft;
      const slideCenter = slideLeft + slide.offsetWidth / 2;
      const distance = Math.abs(centerPoint - slideCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });
    
    // Update active dot
    this.dotLinks.forEach((link, index) => {
      if (index === activeIndex) {
        link.classList.add('slider-counter__link--active');
        link.setAttribute('aria-current', 'true');
      } else {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      }
    });
  }
}

// Initialize all pure-image-blocks sliders when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPureImageBlocksSliders);
} else {
  initPureImageBlocksSliders();
}

function initPureImageBlocksSliders() {
  const sliders = document.querySelectorAll('.pure-image-blocks__slider');
  sliders.forEach(slider => {
    new PureImageBlocksSlider(slider);
  });
}

// Re-initialize when new sections are added (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('.pure-image-blocks__slider');
    if (slider) {
      new PureImageBlocksSlider(slider);
    }
  });
}
